/* eslint-disable react/prop-types */
// Routing section of the Twilio settings modal: map incoming phone numbers to
// caller-type rules, each an ordered list of teams. A rule says "for this number,
// if the CALLER matches this category, ring these teams in this order". The caller's
// number is matched against teacher/student categories; "Any caller" is the fallback
// rule used when no category rule matches. These rules are consumed at call time by
// the inbound dispatcher (goserver twilio_call.orderAgentsByRouting). Add a rule row by
// picking a phone number, a caller type (defaults to "Any caller"), and a team from
// three searchable dropdowns. Rules are LISTED in evaluation order (specific
// numbers before "Any number", specific caller types before "Any caller"), which
// is display-only and independent of the stored positions. Within a
// (number, caller-type) group, reorder teams
// with ↑/↓ (persisted immediately as the full id list for that group) and remove a
// team (armed-button confirm — no window.confirm). The phone numbers and caller
// types come from the server, so there's no free-text validation. Every mutation is
// super-user only (server-enforced); errors surface as toasts.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FaArrowUp, FaArrowDown, FaTrashAlt, FaTimes, FaPlus } from 'react-icons/fa';
import { set_notification_variable as setNotificationVariable } from '../../../redux/action_creators/home_actions';
import {
  apiErrorReason,
  fetchRouting,
  addRouting,
  deleteRouting,
  reorderRouting
} from './settings_api';
import useArmedConfirm from './settings_hooks';
import SearchableDropdown from './SearchableDropdown';

const DEFAULT_CALLER_TYPE = 'ANY';
// Wildcard phone-number sentinel: a rule keyed to "ANY" matches calls to any
// number as a fallback. The backend accepts it on POST /routing and
// PUT /routing/reorder. GET /routing returns the ANY group first; the list here
// is re-sorted for display so the fallbacks come last (see orderedRouting).
const ANY_NUMBER = 'ANY';
const ANY_NUMBER_LABEL = 'Any number';

export default function RoutingSettings({ teams, teamsStatus, phoneNumbers, callerTypes }) {
  const dispatch = useDispatch();
  const [routing, setRouting] = useState([]);
  const [loadStatus, setLoadStatus] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState('');
  const [callerType, setCallerType] = useState(DEFAULT_CALLER_TYPE);
  const [teamUuid, setTeamUuid] = useState('');
  const [formError, setFormError] = useState('');
  const { armedId, arm, disarm } = useArmedConfirm();

  const teamName = useMemo(() => {
    const m = {};
    teams.forEach((t) => {
      m[t.uuid] = t.name;
    });
    return m;
  }, [teams]);

  // Map a caller_type enum value → its server-supplied label (e.g. ANY → "Any caller",
  // NOMNOT_STUDENT → "NomNot"). Falls back to the raw value for unknown types.
  const callerTypeLabel = useMemo(() => {
    const m = {};
    callerTypes.forEach((c) => {
      m[c.value] = c.label;
    });
    return m;
  }, [callerTypes]);

  // Phone-number dropdown options: the "Any number" wildcard is always offered
  // first (as a fallback rule), followed by the real E.164 numbers from the
  // server. Shaped as {value,label} since the raw list is plain strings.
  const phoneOptions = useMemo(
    () => [
      { value: ANY_NUMBER, label: ANY_NUMBER_LABEL },
      ...phoneNumbers.map((n) => ({ value: n, label: n }))
    ],
    [phoneNumbers]
  );

  // A blocking empty state hides the add form; null means the form is shown.
  // Only a missing team blocks now — the "Any number" option means the phone
  // dropdown is never truly empty, so no-real-numbers no longer blocks the form.
  let blocker = null;
  if (teamsStatus === 'success' && teams.length === 0) {
    blocker = 'Create a team first, then map phone numbers to it.';
  }

  const reload = useCallback(() => {
    setLoadStatus((s) => (s === 'success' ? s : 'loading'));
    return fetchRouting()
      .then((rows) => {
        setRouting(rows);
        setLoadStatus('success');
      })
      .catch(() => setLoadStatus('fail'));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Display order mirrors the evaluation order described in the copy above, so
  // the list reads top-to-bottom the way a call is actually matched: specific
  // phone numbers first with the "Any number" fallback group last, and within
  // each group the specific caller-type rules first (in the server's canonical
  // caller-type order) with "Any caller" last. Purely presentational — nothing
  // here touches stored positions or any API payload. The mutations key off row
  // ids (delete) and the rule's own phone_number + caller_type (add/reorder),
  // and ↑/↓ reorder teams *within* one rule, so rule-level display order is
  // independent of what gets persisted.
  const orderedRouting = useMemo(() => {
    const rank = new Map(callerTypes.map((c, i) => [c.value, i]));
    // "Any caller" always sorts last; an unrecognised type sorts after the
    // known specific types but still ahead of the fallback.
    const callerRank = (ct) => {
      if (ct === DEFAULT_CALLER_TYPE) return Number.MAX_SAFE_INTEGER;
      return rank.has(ct) ? rank.get(ct) : Number.MAX_SAFE_INTEGER - 1;
    };
    // Array#sort is stable, so the real numbers keep the server's ordering
    // (phone_number ASC) and only the ANY group is moved to the end.
    return routing
      .slice()
      .sort((a, b) => {
        const aAny = a.phone_number === ANY_NUMBER;
        const bAny = b.phone_number === ANY_NUMBER;
        if (aAny === bAny) return 0;
        return aAny ? 1 : -1;
      })
      .map((group) => ({
        ...group,
        rules: (group.rules || [])
          .slice()
          .sort((a, b) => callerRank(a.caller_type) - callerRank(b.caller_type))
      }));
  }, [routing, callerTypes]);

  const toast = (err, fallback) =>
    dispatch(setNotificationVariable(true, 'error', apiErrorReason(err, fallback)));

  const onAdd = () => {
    setFormError('');
    if (!phone || !callerType || !teamUuid) {
      setFormError('Pick a phone number, a caller type, and a team to add.');
      return;
    }
    setBusy(true);
    addRouting(phone, teamUuid, callerType)
      .then(() => {
        setPhone('');
        setTeamUuid('');
        setCallerType(DEFAULT_CALLER_TYPE);
        return reload();
      })
      .catch((err) => setFormError(apiErrorReason(err, 'Could not add routing')))
      .finally(() => setBusy(false));
  };

  const onRemove = (row) => {
    const id = `del:${row.id}`;
    // First click arms; second click on the same row performs the delete.
    if (armedId !== id) {
      arm(id);
      return;
    }
    disarm();
    setBusy(true);
    deleteRouting(row.id)
      .then(reload)
      .catch((err) => toast(err, 'Could not remove team from routing'))
      .finally(() => setBusy(false));
  };

  // Swap a team with its neighbour inside one (number, caller-type) rule and
  // persist the full id order for that rule. The new order is applied to local
  // state as soon as the server accepts it rather than waiting on the refetch,
  // so the move is visible immediately and can't be masked by a stale read.
  const onReorder = (phoneNumber, rule, index, dir) => {
    const rows = rule.teams || [];
    const next = index + dir;
    if (next < 0 || next >= rows.length) return;
    const reordered = rows.slice();
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    const ids = reordered.map((r) => r.id);
    setBusy(true);
    reorderRouting(phoneNumber, rule.caller_type, ids)
      .then(() => {
        setRouting((prev) =>
          prev.map((group) =>
            group.phone_number !== phoneNumber
              ? group
              : {
                  ...group,
                  rules: (group.rules || []).map((r) =>
                    r.caller_type !== rule.caller_type ? r : { ...r, teams: reordered }
                  )
                }
          )
        );
        return reload();
      })
      .catch((err) => toast(err, 'Could not reorder teams'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="ta-settings-section">
      <h3 className="ta-settings-heading">Routing</h3>
      <p className="ta-settings-desc ta-settings-desc-lead">
        These rules are live: on an incoming call they decide which team&apos;s agents ring first. A
        call is matched on phone number + caller type, checked in this order:
      </p>
      <ol className="ta-settings-desc-steps">
        <li>Exact number + specific caller type</li>
        <li>Exact number + Any caller</li>
        <li>Any number + specific caller type</li>
        <li>Any number + Any caller</li>
      </ol>
      <p className="ta-settings-desc">
        The first level that has a matching rule wins — &ldquo;Any number&rdquo; and &ldquo;Any
        caller&rdquo; rules apply only when nothing more specific matched, they never override a
        specific rule. The caller&apos;s phone number is looked up to classify them (Teacher, Member
        Student, Trial Student, Nomnot); a caller whose number isn&apos;t recognised matches only
        &ldquo;Any caller&rdquo; rules. Within the matching rule, teams ring in the numbered order
        shown; if none of their agents is free, the remaining available agents are tried as a last
        resort.
      </p>

      {blocker ? (
        <div className="ta-state-msg">{blocker}</div>
      ) : (
        <>
          <div className="ta-routing-form">
            <SearchableDropdown
              options={phoneOptions}
              disabled={busy}
              placeholder="Select phone…"
              searchPlaceholder="Search…"
              noMatchesLabel="No matching numbers"
              selectedLabel={phone === ANY_NUMBER ? ANY_NUMBER_LABEL : phone}
              getKey={(o) => o.value}
              getPrimary={(o) => o.label}
              getSecondary={() => ''}
              getSearchText={(o) => o.label}
              onSelect={(o) => {
                setPhone(o.value);
                if (formError) setFormError('');
              }}
            />
            <SearchableDropdown
              options={callerTypes}
              disabled={busy}
              placeholder="Select a caller type…"
              searchPlaceholder="Search caller types…"
              noMatchesLabel="No matching caller types"
              selectedLabel={callerType ? callerTypeLabel[callerType] || callerType : ''}
              getKey={(c) => c.value}
              getPrimary={(c) => c.label}
              getSecondary={() => ''}
              getSearchText={(c) => c.label || ''}
              onSelect={(c) => {
                setCallerType(c.value);
                if (formError) setFormError('');
              }}
            />
            <SearchableDropdown
              options={teams}
              disabled={busy}
              placeholder="Select a team…"
              searchPlaceholder="Search teams…"
              noMatchesLabel="No matching teams"
              selectedLabel={teamUuid ? teamName[teamUuid] || teamUuid : ''}
              getKey={(t) => t.uuid}
              getPrimary={(t) => t.name}
              getSecondary={() => ''}
              getSearchText={(t) => t.name || ''}
              onSelect={(t) => {
                setTeamUuid(t.uuid);
                if (formError) setFormError('');
              }}
            />
            <button
              type="button"
              className="ta-modal-save"
              onClick={onAdd}
              disabled={busy || !phone || !callerType || !teamUuid}
            >
              <FaPlus size={13} /> Add
            </button>
          </div>
          {formError && <p className="ta-team-error">{formError}</p>}

          {loadStatus === 'loading' && routing.length === 0 && (
            <div className="ta-state-msg">Loading routing…</div>
          )}
          {loadStatus === 'fail' && <div className="ta-state-msg">Couldn&apos;t load routing.</div>}
          {loadStatus === 'success' && routing.length === 0 && (
            <div className="ta-state-msg">No phone numbers mapped yet. Add one above.</div>
          )}

          <div className="ta-route-list">
            {orderedRouting.map((group) => {
              const rules = group.rules || [];
              return (
                <div key={group.phone_number} className="ta-route-group" onMouseLeave={disarm}>
                  <div className="ta-route-phone">
                    {group.phone_number === ANY_NUMBER ? (
                      <span className="ta-route-any-badge">{ANY_NUMBER_LABEL}</span>
                    ) : (
                      group.phone_number
                    )}
                  </div>
                  {rules.length === 0 && <span className="ta-team-empty">No rules mapped.</span>}
                  {rules.map((rule) => {
                    const rows = rule.teams || [];
                    // Ring order only exists once a rule has two or more teams.
                    // With a single team both arrows would be permanently
                    // disabled, so don't render dead controls at all — they read
                    // as broken rather than as "nothing to reorder".
                    const canReorder = rows.length > 1;
                    return (
                      <div key={rule.caller_type} className="ta-route-rule">
                        <div className="ta-route-caller-type">
                          {callerTypeLabel[rule.caller_type] || rule.caller_type}
                          {rule.caller_type === DEFAULT_CALLER_TYPE && (
                            <span
                              className="ta-route-fallback-hint"
                              title="Used only when no specific caller-type rule matched"
                            >
                              fallback
                            </span>
                          )}
                        </div>
                        {rows.length === 0 && (
                          <span className="ta-team-empty">No teams mapped.</span>
                        )}
                        <div className="ta-route-team-list">
                          {rows.map((row, index) => (
                            <div key={row.id} className="ta-route-team-row">
                              <span className="ta-route-pos">{index + 1}</span>
                              <span className="ta-route-team-name">
                                {row.name || teamName[row.team_uuid] || row.team_uuid}
                              </span>
                              <span className="ta-route-controls">
                                {canReorder && (
                                  <>
                                    <button
                                      type="button"
                                      className="ta-icon-btn"
                                      aria-label="Move up"
                                      title={index === 0 ? 'Already first' : 'Move up'}
                                      onClick={() => onReorder(group.phone_number, rule, index, -1)}
                                      disabled={busy || index === 0}
                                    >
                                      <FaArrowUp size={12} />
                                    </button>
                                    <button
                                      type="button"
                                      className="ta-icon-btn"
                                      aria-label="Move down"
                                      title={
                                        index === rows.length - 1 ? 'Already last' : 'Move down'
                                      }
                                      onClick={() => onReorder(group.phone_number, rule, index, 1)}
                                      disabled={busy || index === rows.length - 1}
                                    >
                                      <FaArrowDown size={12} />
                                    </button>
                                  </>
                                )}
                                <span className="ta-del-wrap">
                                  <button
                                    type="button"
                                    className={`ta-team-del ${
                                      armedId === `del:${row.id}` ? 'ta-del-armed' : ''
                                    }`}
                                    aria-label={
                                      armedId === `del:${row.id}`
                                        ? 'Confirm remove team from routing'
                                        : 'Remove team from routing'
                                    }
                                    title={
                                      armedId === `del:${row.id}`
                                        ? 'Click again to confirm'
                                        : 'Remove team'
                                    }
                                    onClick={() => onRemove(row)}
                                    disabled={busy}
                                  >
                                    {armedId === `del:${row.id}` ? (
                                      <>
                                        <FaTrashAlt size={12} /> Confirm?
                                      </>
                                    ) : (
                                      <FaTrashAlt size={13} />
                                    )}
                                  </button>
                                  {armedId === `del:${row.id}` && (
                                    <button
                                      type="button"
                                      className="ta-arm-cancel"
                                      aria-label="Cancel"
                                      title="Cancel"
                                      onClick={disarm}
                                      disabled={busy}
                                    >
                                      <FaTimes size={13} />
                                    </button>
                                  )}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
